import { api } from './api';

// Live-traffic types mirror the traffic-service Go structs exactly
// (backend/traffic-service/main.go: TrafficFlow, Incident, GeoJSON).
//
// GET /traffic/flow?bbox=west,south,east,north
//   -> { status, data: TrafficFlow[], meta: { count, bbox, updated_at } }
// GET /traffic/incidents?bbox=west,south,east,north
//   -> { status, data: TrafficIncident[] }
//
// Both endpoints require a `bbox` query param ("w,s,e,n"); a missing/invalid
// bbox yields HTTP 400. Served flow geometry is always a GeoJSON Point
// ([lng,lat]) representing the segment; served conditions are on the
// {light, moderate, heavy, severe} scale (free-flow is collapsed into "light").

// A GeoJSON Point as emitted by the service ({ type:"Point", coordinates:[lng,lat] }).
export interface GeoJSONPoint {
  type: string;
  coordinates: [number, number];
}

export type TrafficCondition = 'free' | 'light' | 'moderate' | 'heavy' | 'severe';

export interface TrafficFlow {
  segment_id: string;
  geometry: GeoJSONPoint | null;
  current_speed: number;
  free_flow_speed: number;
  congestion_level: number; // 0..1 (higher = more congested)
  condition: string; // TrafficCondition on the served scale
  updated_at: string;
}

export interface TrafficIncident {
  id: string;
  type: string; // e.g. "accident" | "construction" | "hazard" | ...
  severity: string; // "light" | "moderate" | "heavy" | "severe"
  description: string;
  location: GeoJSONPoint | null;
  start_time: string;
  end_time?: string;
  verified: boolean;
}

// Map viewport bounds as a "west,south,east,north" bbox string for the API.
export interface Bbox {
  west: number;
  south: number;
  east: number;
  north: number;
}

function bboxParam(b: Bbox): string {
  return `${b.west},${b.south},${b.east},${b.north}`;
}

export const trafficService = {
  // Live congestion flow segments within the viewport. Returns [] on empty/failed
  // responses so callers never render fabricated data. Uses a stable abortKey so a
  // newer viewport request transparently cancels the previous in-flight one.
  async getFlow(bbox: Bbox): Promise<TrafficFlow[]> {
    const res = await api.get<TrafficFlow[]>('/traffic/flow', {
      params: { bbox: bboxParam(bbox) },
      abortKey: 'traffic:flow',
    });
    return Array.isArray(res.data) ? res.data : [];
  },

  // Live incidents (accidents/construction/etc.) within the viewport.
  async getIncidents(bbox: Bbox): Promise<TrafficIncident[]> {
    const res = await api.get<TrafficIncident[]>('/traffic/incidents', {
      params: { bbox: bboxParam(bbox) },
      abortKey: 'traffic:incidents',
    });
    return Array.isArray(res.data) ? res.data : [];
  },
};

export default trafficService;
